/**
 * Comercial catalog screens.
 *
 * Products and services are separate catalog entities in the backend:
 * - products: one-off catalog items, with SKU.
 * - services: recurring/cycle-based catalog items, with periodicidade.
 *
 * Deal items keep a product_id XOR service_id reference, so existing sold items
 * remain classified by the reference that is already stored in deal_items.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import Layout from '../../components/Layout.jsx'
import Icon from '../../components/icons.jsx'
import { SearchInput } from '../../components/UX.jsx'
import {
  getProducts, createProduct, updateProduct, deleteProduct,
  getServices, createService, updateService, deleteService,
  fmtBRL,
} from '../../db/api.js'
import { fmtDate } from './panelKit.jsx'
import { useApp } from '../../context/AppContext.jsx'

const PERIODICIDADES = ['mensal', 'trimestral', 'semestral', 'anual', 'unico']

const CATALOG = {
  produtos: {
    title: 'Produtos',
    singular: 'produto',
    plural: 'produtos',
    newLabel: 'Novo produto',
    emptyClient: 'Selecione um cliente para ver os produtos.',
    emptyList: 'Nenhum produto cadastrado ainda.',
    searchPlaceholder: 'Buscar produtos...',
    deleteMessage: (item) => `Desativar o produto "${item.nome}"? Ele deixa de aparecer na lista de produtos.`,
    metaLabel: 'SKU',
    metaValue: (item) => item.sku || '-',
    priceLabel: 'Preço (R$)',
    load: getProducts,
    create: createProduct,
    update: updateProduct,
    deactivate: deleteProduct,
  },
  servicos: {
    title: 'Serviços',
    singular: 'serviço',
    plural: 'serviços',
    newLabel: 'Novo serviço',
    emptyClient: 'Selecione um cliente para ver os serviços.',
    emptyList: 'Nenhum serviço cadastrado ainda.',
    searchPlaceholder: 'Buscar serviços...',
    deleteMessage: (item) => `Desativar o serviço "${item.nome}"? Ele deixa de aparecer na lista de serviços.`,
    metaLabel: 'Ciclo',
    metaValue: (item) => item.periodicidade || '-',
    priceLabel: 'Preço por ciclo (R$)',
    load: getServices,
    create: createService,
    update: updateService,
    deactivate: deleteService,
  },
}

export function Produtos() {
  return <CatalogPage type="produtos" />
}

export function Servicos() {
  return <CatalogPage type="servicos" />
}


function CatalogPage({ type }) {
  const config = CATALOG[type]
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [search, setSearch] = useState('')
  const [modal, setModal] = useState(null)

  const { selectedClient } = useApp()
  const clientId = selectedClient?.id || null
  const hasClient = !!clientId

  const load = useCallback(async () => {
    if (!clientId) {
      setItems([])
      setLoading(false)
      return
    }
    setLoading(true)
    setErr(null)
    try {
      setItems(await config.load())
    } catch (e) {
      setErr(e.message)
    } finally {
      setLoading(false)
    }
  }, [clientId, config])

  useEffect(() => {
    setModal(null)
    setSearch('')
    load()
  }, [load])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return items
    return items.filter((item) => (
      [item.nome, item.descricao, item.sku, item.categoria, item.periodicidade]
        .some((value) => (value || '').toLowerCase().includes(q))
    ))
  }, [items, search])

  const handleDelete = async (item) => {
    if (!window.confirm(config.deleteMessage(item))) return
    try {
      await config.deactivate(item.id)
      await load()
    } catch (e) {
      alert(`Erro ao desativar: ${e.message}`)
    }
  }

  if (!hasClient) {
    return (
      <Layout title={config.title} breadcrumb={[{ label: 'Comercial' }, { label: config.title }]}>
        <Empty msg={config.emptyClient} />
      </Layout>
    )
  }

  return (
    <Layout title={config.title} breadcrumb={[{ label: 'Comercial' }, { label: config.title }]}>
      <div className="ga-filters-bar" style={{ marginBottom: 16 }}>
        <div style={S.summary}>
          <span style={S.count}>{items.length}</span>
          <span>{items.length === 1 ? config.singular : config.plural}</span>
        </div>
        <div style={S.barRight}>
          <div style={S.searchBox}>
            <SearchInput placeholder={config.searchPlaceholder} value={search} onChange={setSearch} />
          </div>
          <button style={S.btnPrimary} onClick={() => setModal({ item: null })}>
            <Icon n="plus" s={15} sw={2} /> {config.newLabel}
          </button>
        </div>
      </div>

      {err && <div style={S.error}>{err}</div>}
      {loading ? (
        <div style={S.muted}>Carregando...</div>
      ) : !filtered.length ? (
        <Empty msg={search ? 'Nenhum item corresponde a busca.' : config.emptyList} />
      ) : (
        <div style={S.list}>
          {filtered.map((item) => (
            <div key={item.id} style={S.rowCard}>
              <div style={S.colName}>
                <div style={S.name}>{item.nome}</div>
                {(item.categoria || item.descricao) && (
                  <div style={S.sub}>{[item.categoria, item.descricao].filter(Boolean).join(' · ')}</div>
                )}
              </div>

              <div style={S.colMeta}>
                <span style={S.metaLabel}>{config.metaLabel}</span>
                <span style={S.metaValue}>{config.metaValue(item)}</span>
              </div>

              <div style={S.colMeta}>
                <span style={S.metaLabel}>Preço</span>
                <span style={S.metaPreco}>{item.preco != null ? fmtBRL(item.preco) : '-'}</span>
              </div>

              <div style={S.colMeta}>
                <span style={S.metaLabel}>Criado em</span>
                <span style={S.metaValue}>{fmtDate(item.created_at)}</span>
              </div>

              <div style={S.actions}>
                <button style={S.iconBtn} title="Editar" onClick={() => setModal({ item })}>
                  <Icon n="edit" s={16} />
                </button>
                <button style={S.iconBtn} title="Excluir" onClick={() => handleDelete(item)}>
                  <Icon n="trash" s={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <CatalogModal
          type={type}
          item={modal.item}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load() }}
        />
      )}
    </Layout>
  )
}

function Empty({ msg }) {
  return <div style={{ ...S.muted, padding: '48px 16px', textAlign: 'center' }}>{msg}</div>
}

function CatalogModal({ type, item, onClose, onSaved }) {
  const config = CATALOG[type]
  const isProduct = type === 'produtos'
  const isEdit = !!item
  const [nome, setNome] = useState(item?.nome || '')
  const [descricao, setDescricao] = useState(item?.descricao || '')
  const [categoria, setCategoria] = useState(item?.categoria || '')
  const [preco, setPreco] = useState(item?.preco != null ? String(item.preco) : '')
  const [sku, setSku] = useState(item?.sku || '')
  const [periodicidade, setPeriodicidade] = useState(item?.periodicidade || 'mensal')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  const save = async () => {
    if (saving) return
    if (!nome.trim()) {
      setErr('Nome é obrigatório.')
      return
    }
    setSaving(true)
    setErr(null)
    const base = {
      nome: nome.trim(),
      descricao: descricao.trim() || null,
      categoria: categoria.trim() || null,
      preco: preco === '' ? null : Number(preco),
    }
    const data = isProduct
      ? { ...base, sku: sku.trim() || null }
      : { ...base, periodicidade }
    try {
      if (isEdit) await config.update(item.id, data)
      else await config.create(data)
      onSaved()
    } catch (e) {
      setErr(e.message)
      setSaving(false)
    }
  }

  const title = `${isEdit ? 'Editar' : 'Novo'} ${config.singular}`

  return (
    <div style={S.overlay} onMouseDown={onClose}>
      <div style={S.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div style={S.head}>
          <h3 style={S.modalTitle}>{title}</h3>
          <button style={S.x} onClick={onClose}>×</button>
        </div>
        {err && <div style={S.error}>{err}</div>}

        <label style={S.label}>Nome *</label>
        <input style={S.input} value={nome} onChange={(e) => setNome(e.target.value)} autoFocus />

        <label style={S.label}>Descrição</label>
        <textarea style={{ ...S.input, minHeight: 56, resize: 'vertical' }}
          value={descricao} onChange={(e) => setDescricao(e.target.value)} />

        <label style={S.label}>Categoria</label>
        <input style={S.input} value={categoria} onChange={(e) => setCategoria(e.target.value)}
          placeholder="Ex.: Consultoria, Tráfego, Design..." />

        <div style={S.row}>
          <div style={{ flex: 1 }}>
            <label style={S.label}>{config.priceLabel}</label>
            <input style={S.input} type="number" value={preco}
              onChange={(e) => setPreco(e.target.value)} placeholder="0,00" />
          </div>
          {isProduct ? (
            <div style={{ flex: 1 }}>
              <label style={S.label}>SKU</label>
              <input style={S.input} value={sku} onChange={(e) => setSku(e.target.value)} />
            </div>
          ) : (
            <div style={{ flex: 1 }}>
              <label style={S.label}>Periodicidade</label>
              <select style={S.input} value={periodicidade} onChange={(e) => setPeriodicidade(e.target.value)}>
                {PERIODICIDADES.map((period) => <option key={period} value={period}>{period}</option>)}
              </select>
            </div>
          )}
        </div>

        <div style={S.footer}>
          <button style={S.btnGhost} onClick={onClose}>Cancelar</button>
          <button style={S.btnPrimary} onClick={save} disabled={saving}>
            {saving ? 'Salvando...' : isEdit ? 'Salvar' : 'Criar'}
          </button>
        </div>
      </div>
    </div>
  )
}

const S = {
  summary: {
    display: 'inline-flex', alignItems: 'center', gap: 8, flex: 1,
    color: 'var(--sec)', fontSize: 13, fontWeight: 600,
  },
  count: {
    fontSize: 11, fontWeight: 700, color: 'var(--ter)', background: 'var(--surface)',
    border: '1px solid var(--soft)', borderRadius: 100, padding: '1px 7px',
  },
  barRight: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  searchBox: { minWidth: 200, maxWidth: 320 },

  list: { display: 'flex', flexDirection: 'column', gap: 8 },
  rowCard: {
    display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap',
    padding: '12px 16px', borderRadius: 12, border: '1px solid var(--soft)',
    background: 'var(--surface)',
  },
  colName: { flex: '1 1 200px', minWidth: 0 },
  name: { fontSize: 14.5, fontWeight: 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  sub: { fontSize: 12, color: 'var(--sec)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  colMeta: { display: 'flex', flexDirection: 'column', gap: 2, flex: '0 0 auto', minWidth: 88 },
  metaLabel: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--ter)' },
  metaValue: { fontSize: 13, color: 'var(--sec)', fontWeight: 500 },
  metaPreco: { fontSize: 14, color: 'var(--text)', fontWeight: 700 },
  actions: { display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto', flexShrink: 0 },
  iconBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32,
    borderRadius: 8, border: '1px solid var(--soft)', background: 'var(--surface)',
    color: 'var(--sec)', cursor: 'pointer',
  },

  muted: { color: 'var(--sec)', fontSize: 14 },
  error: {
    padding: '10px 14px', borderRadius: 8, background: 'var(--amber-bg, #fdecea)',
    border: '1px solid var(--amber-border, #f5c6cb)', color: 'var(--amber, #c0392b)', marginBottom: 12, fontSize: 13,
  },
  btnPrimary: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
    border: '1px solid var(--text)', background: 'var(--text)', color: 'var(--surface)',
    fontSize: 13, fontWeight: 700, cursor: 'pointer',
  },
  btnGhost: {
    padding: '8px 14px', borderRadius: 8, border: '1px solid var(--soft)',
    background: 'var(--surface)', color: 'var(--text)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },

  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000,
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '8vh 16px',
  },
  modal: {
    width: 460, maxWidth: '100%', background: 'var(--surface)', border: '1px solid var(--soft)',
    borderRadius: 14, padding: 20, boxShadow: '0 12px 40px rgba(0,0,0,.25)',
  },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  modalTitle: { margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text)' },
  x: { border: 'none', background: 'transparent', fontSize: 24, lineHeight: 1, cursor: 'pointer', color: 'var(--sec)' },
  label: { display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--sec)', margin: '10px 0 4px' },
  input: {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8,
    border: '1px solid var(--soft)', background: 'var(--bg)', color: 'var(--text)', fontSize: 14,
  },
  row: { display: 'flex', gap: 10 },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 },
}
